import { useEffect, useMemo, useState } from 'react';
import { GitBranch, Play, Plus, Trash2, AlertCircle, Power, PowerOff } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { workflowApi, ApiError } from '@solara/api-client';
import type {
  WorkflowDefinitionDto,
  WorkflowInstanceDto,
  WorkflowStepConfigDto,
  WorkflowTrigger,
} from '@solara/api-client';

const TRIGGER_KEYS: readonly WorkflowTrigger[] = [
  'MANUAL',
  'INVOICE_CREATED',
  'INVOICE_RECEIVED',
  'PERIOD_END',
  'DOCUMENT_UPLOADED',
  'KYC_SUBMITTED',
  'TAX_FILING_READY',
  'CUSTOM',
];

interface DraftWorkflow {
  id?: string;
  name: string;
  description: string;
  triggerEvent: WorkflowTrigger;
  triggerCondition: string;
  scope: 'TENANT' | 'CUSTOMER';
  steps: WorkflowStepConfigDto[];
}

const EMPTY_DRAFT: DraftWorkflow = {
  name: '',
  description: '',
  triggerEvent: 'MANUAL',
  triggerCondition: '',
  scope: 'TENANT',
  steps: [{ name: 'Review', assigneeRule: 'ROLE:CustomerAdmin', action: 'review', dueDays: 3 }],
};

export default function WorkflowDefinitionsPage() {
  const { t } = useTranslation('p1');
  const [definitions, setDefinitions] = useState<WorkflowDefinitionDto[]>([]);
  const [instances, setInstances] = useState<WorkflowInstanceDto[]>([]);
  const [triggers, setTriggers] = useState<WorkflowTrigger[]>([]);
  const [draft, setDraft] = useState<DraftWorkflow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const trigLabel = (tg: WorkflowTrigger) => t(`workflow.triggers.${tg}`, { defaultValue: tg });

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [defs, insts, trigs] = await Promise.all([
        workflowApi.list(),
        workflowApi.instances(),
        workflowApi.triggers(),
      ]);
      setDefinitions(defs);
      setInstances(insts);
      setTriggers(trigs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  function startEdit(def: WorkflowDefinitionDto) {
    setDraft({
      id: def.id,
      name: def.name,
      description: def.description ?? '',
      triggerEvent: def.triggerEvent,
      triggerCondition: def.triggerCondition ?? '',
      scope: def.scope,
      steps: (Array.isArray(def.stepsConfig) ? def.stepsConfig : []).map((s) => ({ ...s })),
    });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: draft.name,
        description: draft.description || undefined,
        triggerEvent: draft.triggerEvent,
        triggerCondition: draft.triggerCondition || undefined,
        scope: draft.scope,
        steps: draft.steps,
      };
      if (draft.id) {
        await workflowApi.update(draft.id, body);
      } else {
        await workflowApi.create(body);
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(def: WorkflowDefinitionDto) {
    setBusy(true);
    try {
      await workflowApi.update(def.id, { isActive: !def.isActive });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(def: WorkflowDefinitionDto) {
    if (!window.confirm(t('workflow.confirmDelete', { name: def.name }))) return;
    setBusy(true);
    try {
      await workflowApi.remove(def.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function runManual(def: WorkflowDefinitionDto) {
    const entityType = window.prompt(t('workflow.runEntityType'), 'invoice');
    if (!entityType) return;
    const entityId = window.prompt(t('workflow.runEntityId'), '');
    if (!entityId) return;
    setBusy(true);
    try {
      await workflowApi.start(def.id, { triggerEntityType: entityType, triggerEntityId: entityId });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  function updateStep(idx: number, patch: Partial<WorkflowStepConfigDto>) {
    if (!draft) return;
    const next = draft.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setDraft({ ...draft, steps: next });
  }

  function addStep() {
    if (!draft) return;
    setDraft({
      ...draft,
      steps: [
        ...draft.steps,
        { name: 'Approve', assigneeRule: 'ROLE:CustomerAdmin', action: 'approve', dueDays: 5 },
      ],
    });
  }

  function removeStep(idx: number) {
    if (!draft) return;
    setDraft({ ...draft, steps: draft.steps.filter((_, i) => i !== idx) });
  }

  const totalInstances = useMemo(() => instances.length, [instances]);
  const activeInstances = useMemo(
    () => instances.filter((i) => i.status === 'RUNNING' || i.status === 'PENDING').length,
    [instances],
  );

  return (
    <div className="space-y-6 max-w-6xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('workflow.title')}</h1>
          <p className="text-fg-muted text-sm">{t('workflow.subtitle')}</p>
        </div>
        <Button onClick={() => setDraft({ ...EMPTY_DRAFT })}>
          <Plus className="mr-2 h-4 w-4" />
          {t('workflow.newWorkflow')}
        </Button>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('workflow.definitions')}</p>
            <p className="text-3xl font-semibold mt-2">{definitions.length}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('workflow.totalInstances')}</p>
            <p className="text-3xl font-semibold mt-2">{totalInstances}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('workflow.activeRuns')}</p>
            <p className="text-3xl font-semibold mt-2">{activeInstances}</p>
          </CardBody>
        </Card>
      </div>

      {draft && (
        <Card className="rounded-2xl border-brand-500/30">
          <CardHeader>
            <CardTitle>{draft.id ? t('workflow.editWorkflow') : t('workflow.newWorkflow')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label={t('common.name')}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('workflow.trigger')}</span>
                <select
                  className="w-full mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.triggerEvent}
                  onChange={(e) =>
                    setDraft({ ...draft, triggerEvent: e.target.value as WorkflowTrigger })
                  }
                >
                  {(triggers.length ? triggers : TRIGGER_KEYS).map((tg) => (
                    <option key={tg} value={tg}>
                      {trigLabel(tg)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              <span className="text-xs text-fg-muted">{t('common.description')}</span>
              <textarea
                className="w-full min-h-[4rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label={t('workflow.triggerCondition')}
                value={draft.triggerCondition}
                onChange={(e) => setDraft({ ...draft, triggerCondition: e.target.value })}
                placeholder="e.g. amount > 100000"
              />
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('workflow.scope')}</span>
                <select
                  className="w-full mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.scope}
                  onChange={(e) =>
                    setDraft({ ...draft, scope: e.target.value as 'TENANT' | 'CUSTOMER' })
                  }
                >
                  <option value="TENANT">{t('workflow.scopeTenant')}</option>
                  <option value="CUSTOMER">{t('workflow.scopePerCustomer')}</option>
                </select>
              </label>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                  {t('workflow.stepsCount', { count: draft.steps.length })}
                </span>
                <Button variant="ghost" onClick={addStep}>
                  <Plus className="mr-1 h-3 w-3" />
                  {t('actions.addStep')}
                </Button>
              </div>
              {draft.steps.map((s, i) => (
                <Card key={i} className="rounded-xl border-border/60">
                  <CardBody className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input
                        label={t('workflow.stepName', { n: i + 1 })}
                        value={s.name}
                        onChange={(e) => updateStep(i, { name: e.target.value })}
                      />
                      <Input
                        label={t('workflow.actionLabel')}
                        value={s.action}
                        onChange={(e) => updateStep(i, { action: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Input
                        label={t('workflow.assigneeRule')}
                        value={s.assigneeRule}
                        onChange={(e) => updateStep(i, { assigneeRule: e.target.value })}
                        placeholder={t('workflow.assigneeHint')}
                      />
                      <Input
                        label={t('common.dueDays')}
                        type="number"
                        value={String(s.dueDays ?? '')}
                        onChange={(e) =>
                          updateStep(i, { dueDays: e.target.value ? Number(e.target.value) : undefined })
                        }
                      />
                      <Input
                        label={t('workflow.escalation')}
                        value={s.escalation ?? ''}
                        onChange={(e) => updateStep(i, { escalation: e.target.value || undefined })}
                        placeholder={t('workflow.escalationHint')}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button variant="ghost" onClick={() => removeStep(i)}>
                        <Trash2 className="mr-1 h-3 w-3" />
                        {t('actions.remove')}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>

            <div className="flex gap-2">
              <Button onClick={save} disabled={busy || !draft.name || draft.steps.length === 0}>
                {busy
                  ? t('actions.saving')
                  : draft.id
                    ? t('actions.saveChanges')
                    : t('actions.create')}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            {t('workflow.definitionsCount', { count: definitions.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {definitions.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('workflow.noWorkflows')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {definitions.map((d) => (
                <li key={d.id} className="py-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{d.name}</p>
                        <Badge tone={d.isActive ? 'success' : 'neutral'} className="text-[10px]">
                          {d.isActive ? t('common.active') : t('common.inactive')}
                        </Badge>
                        <Badge tone="info" className="text-[10px]">
                          {trigLabel(d.triggerEvent)}
                        </Badge>
                        <Badge tone="neutral" className="text-[10px]">
                          {d.scope === 'TENANT'
                            ? t('workflow.scopeTenant')
                            : t('workflow.scopePerCustomer')}
                        </Badge>
                      </div>
                      {d.description && (
                        <p className="text-xs text-fg-muted mt-1">{d.description}</p>
                      )}
                      <p className="text-xs text-fg-muted mt-1">
                        {t('workflow.stepCountLabel', {
                          count: Array.isArray(d.stepsConfig) ? d.stepsConfig.length : 0,
                          runs: d._count?.instances ?? 0,
                          date: new Date(d.updatedAt).toLocaleDateString(),
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" onClick={() => void runManual(d)} disabled={busy || !d.isActive}>
                        <Play className="mr-1 h-3 w-3" />
                        {t('workflow.runManual')}
                      </Button>
                      <Button variant="ghost" onClick={() => void toggleActive(d)} disabled={busy}>
                        {d.isActive ? (
                          <PowerOff className="mr-1 h-3 w-3" />
                        ) : (
                          <Power className="mr-1 h-3 w-3" />
                        )}
                        {d.isActive ? t('actions.pause') : t('actions.activate')}
                      </Button>
                      <Button variant="ghost" onClick={() => startEdit(d)}>
                        {t('actions.edit')}
                      </Button>
                      <Button variant="ghost" onClick={() => void remove(d)} disabled={busy}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('workflow.recentInstances', { count: instances.length })}</CardTitle>
        </CardHeader>
        <CardBody>
          {instances.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('workflow.noInstances')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {instances.slice(0, 20).map((i) => (
                <li key={i.id} className="py-2 text-sm flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {i.definition?.name ?? '(workflow)'} ·{' '}
                      <span className="text-fg-muted">
                        {t('workflow.step')} {i.currentStepNumber}
                      </span>
                    </p>
                    <p className="text-xs text-fg-muted">
                      {i.triggerEntityType} / {i.triggerEntityId} — started{' '}
                      {new Date(i.startedAt).toLocaleString()}
                    </p>
                  </div>
                  <Badge
                    tone={
                      i.status === 'COMPLETED'
                        ? 'success'
                        : i.status === 'CANCELLED' || i.status === 'FAILED'
                          ? 'danger'
                          : 'info'
                    }
                    className="text-[10px]"
                  >
                    {i.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
